import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { resolveImageUrl } from "../../utils/imageUrl";
import { ViewOnlyBanner } from "./ViewOnlyBanner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Eye, Palette, ToggleLeft, Type, RefreshCw, Save, Loader2,
  Monitor, Layout, Paintbrush, Undo2,
  Globe, ExternalLink, CheckCircle,
  Image as ImageIcon, History, RotateCcw, Trash2, Upload, X,
  PartyPopper, FileDown, School
} from "lucide-react";
import { DecorationsManager } from "./DecorationsManager";
import { ForSchoolsCMS } from "./ForSchoolsCMS";
import { LivePreviewEditor } from "./LivePreviewEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || "";

const COLOR_FIELDS = {
  "Brand Colors": {
    primary: "Primary Color",
    secondary: "Secondary Color",
    accent: "Accent / CTA",
  },
  "Backgrounds": {
    background: "Page Background",
    surface: "Surface / Cards",
    header_bg: "Header Background",
    footer_bg: "Footer Background",
  },
  "Text Colors": {
    text_primary: "Primary Text",
    text_secondary: "Secondary Text",
    header_text: "Header Text",
    footer_text: "Footer Text",
    link: "Links",
  },
  "UI Elements": {
    button_bg: "Button Background",
    button_text: "Button Text",
    border: "Borders",
    success: "Success",
    warning: "Warning",
    error: "Error / Danger",
  },
};

const SECTION_GROUPS = {
  homepage: { label: "Homepage Sections", icon: Globe },
  layout: { label: "Layout & Navigation", icon: Layout },
};

export const CustomerViewTab = ({ darkMode, viewOnly }) => {
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("preview");
  const [previewKey, setPreviewKey] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [showDecoPreview, setShowDecoPreview] = useState(false);
  const [previewDecorations, setPreviewDecorations] = useState([]);
  const iframeRef = useRef(null);
  const designIframeRef = useRef(null);
  const [designViewport, setDesignViewport] = useState("desktop");
  const [designDecoPreview, setDesignDecoPreview] = useState(false);
  const [designPreviewKey, setDesignPreviewKey] = useState(0);
  const [designIframeLoaded, setDesignIframeLoaded] = useState(false);
  const [designTabVisited, setDesignTabVisited] = useState(false);

  const token = localStorage.getItem("gmp_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/site-customization`, { headers });
      setConfig(res.data);
      setOriginalConfig(JSON.parse(JSON.stringify(res.data)));
      // Check for draft
      const draftRes = await axios.get(`${API}/admin/site-customization/draft`, { headers });
      setHasDraft(draftRes.data.has_draft);
    } catch (err) {
      console.error("Failed to fetch site customization:", err);
      toast.error("Failed to load site customization");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  useEffect(() => {
    if (config && originalConfig) {
      setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  // Send live preview updates to iframe via postMessage
  useEffect(() => {
    if (!config) return;
    const payload = {
      type: "SITE_CUSTOMIZATION_PREVIEW",
      payload: {
        theme_colors: config.theme_colors,
        section_config: config.section_config,
        content_blocks: config.content_blocks,
        images: config.images,
      },
    };
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(payload, "*");
    }
    if (designIframeRef.current?.contentWindow) {
      designIframeRef.current.contentWindow.postMessage(payload, "*");
    }
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-customization`, {
        theme_colors: config.theme_colors,
        section_config: config.section_config,
        content_blocks: config.content_blocks,
        images: config.images,
      }, { headers });
      setOriginalConfig(JSON.parse(JSON.stringify(config)));
      setHasChanges(false);
      setPreviewKey(k => k + 1);
      toast.success("Site customization saved & published!");
      fetchHistory();
    } catch (err) {
      toast.error("Failed to save customization");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(JSON.parse(JSON.stringify(originalConfig)));
    setHasChanges(false);
    toast.info("Changes reverted");
  };

  const updateGlobalColor = (key, value) => {
    setConfig(prev => ({
      ...prev,
      theme_colors: {
        ...prev.theme_colors,
        global: { ...prev.theme_colors.global, [key]: value },
      },
    }));
  };

  const updateSectionOverride = (sectionKey, colorKey, value) => {
    setConfig(prev => ({
      ...prev,
      theme_colors: {
        ...prev.theme_colors,
        overrides: {
          ...prev.theme_colors.overrides,
          [sectionKey]: {
            ...(prev.theme_colors.overrides?.[sectionKey] || {}),
            [colorKey]: value,
          },
        },
      },
    }));
  };

  const removeSectionOverride = (sectionKey) => {
    setConfig(prev => {
      const newOverrides = { ...prev.theme_colors.overrides };
      delete newOverrides[sectionKey];
      return { ...prev, theme_colors: { ...prev.theme_colors, overrides: newOverrides } };
    });
    setEditingOverride(null);
  };

  const toggleSection = (sectionKey) => {
    setConfig(prev => ({
      ...prev,
      section_config: {
        ...prev.section_config,
        [sectionKey]: {
          ...prev.section_config[sectionKey],
          enabled: !prev.section_config[sectionKey]?.enabled,
        },
      },
    }));
  };

  const updateContent = (blockKey, value) => {
    setConfig(prev => ({
      ...prev,
      content_blocks: { ...prev.content_blocks, [blockKey]: value },
    }));
  };

  const updateImage = (imageKey, url) => {
    setConfig(prev => ({
      ...prev,
      images: { ...prev.images, [imageKey]: url },
    }));
  };

  const handleImageUpload = async (imageKey, file) => {
    setUploading(imageKey);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/site-customization/upload-image`, formData, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      updateImage(imageKey, res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(null);
    }
  };

  const removeImage = (imageKey) => {
    updateImage(imageKey, "");
  };

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/admin/site-customization/history`, { headers });
      setHistory(res.data);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const fetchDecorations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/decorations`, { headers });
      setPreviewDecorations(res.data);
    } catch (err) {
      console.error("Failed to fetch decorations for preview:", err);
    }
  }, []);

  const toggleDecorationFromPreview = async (decId, currentActive) => {
    try {
      await axios.put(`${API}/admin/decorations/${decId}`, { is_active: !currentActive }, { headers });
      setPreviewDecorations(prev => prev.map(d => d.id === decId ? { ...d, is_active: !currentActive } : d));
      toast.success(`Decoration ${!currentActive ? "activated" : "deactivated"}`);
    } catch (err) {
      toast.error("Failed to toggle decoration");
    }
  };

  // Inject unpublished theme CSS into the design preview iframe
  const injectThemeIntoIframe = useCallback(() => {
    const iframe = designIframeRef.current;
    if (!iframe || !config) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!doc) return;
      const g = config.theme_colors?.global || {};
      const vars = {
        "--color-site-primary": g.primary || "#0f172a",
        "--color-site-secondary": g.secondary || "#64748b",
        "--color-site-accent": g.accent || "#e0482d",
        "--color-site-background": g.background || "#ffffff",
        "--color-site-surface": g.surface || "#f8fafc",
        "--color-site-text-primary": g.text_primary || "#0f172a",
        "--color-site-text-secondary": g.text_secondary || "#64748b",
        "--color-site-border": g.border || "#e2e8f0",
        "--color-site-button-bg": g.button_bg || "#e0482d",
        "--color-site-button-text": g.button_text || "#ffffff",
        "--color-site-header-bg": g.header_bg || "#0f172a",
        "--color-site-header-text": g.header_text || "#ffffff",
        "--color-site-footer-bg": g.footer_bg || "#0f172a",
        "--color-site-footer-text": g.footer_text || "#ffffff",
        "--color-site-link": g.link || "#e0482d",
      };
      const root = doc.documentElement;
      Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));

      // Inject targeted CSS overrides with hardcoded colors (not variables)
      // so they persist even after iframe's own applyThemeColors overwrites CSS variables
      let themeStyle = doc.getElementById("gmp-design-preview-theme");
      if (!themeStyle) {
        themeStyle = doc.createElement("style");
        themeStyle.id = "gmp-design-preview-theme";
        doc.head.appendChild(themeStyle);
      }

      const primary = g.primary || "#0f172a";
      const secondary = g.secondary || "#64748b";
      const accent = g.accent || "#e0482d";
      const background = g.background || "#ffffff";
      const surface = g.surface || "#f8fafc";
      const textPrimary = g.text_primary || "#0f172a";
      const textSecondary = g.text_secondary || "#64748b";
      const borderColor = g.border || "#e2e8f0";
      const buttonBg = g.button_bg || "#e0482d";
      const buttonText = g.button_text || "#ffffff";
      const headerBg = g.header_bg || "#0f172a";
      const headerText = g.header_text || "#ffffff";
      const footerBg = g.footer_bg || "#0f172a";
      const footerText = g.footer_text || "#ffffff";
      const linkColor = g.link || "#e0482d";

      themeStyle.textContent = `
        /* === PAGE BACKGROUND === */
        body { background-color: ${background} !important; }
        section.bg-white { background-color: ${background} !important; }
        section.bg-gray-50 { background-color: ${surface} !important; }

        /* === SURFACE / CARDS === */
        .bg-gray-50 { background-color: ${surface} !important; }
        .feature-card.bg-white, .bg-white.rounded-2xl { background-color: ${background} !important; }

        /* === PRIMARY COLOR (navy-900 backgrounds) === */
        .bg-navy-900 { background-color: ${primary} !important; }
        .bg-navy-800 { background-color: ${primary} !important; }
        .hover\\:bg-navy-900:hover { background-color: ${primary} !important; }
        .hover\\:bg-navy-800:hover { background-color: ${primary} !important; }

        /* === ACCENT / CTA COLOR === */
        .bg-accent { background-color: ${accent} !important; }
        .hover\\:bg-accent\\/90:hover { background-color: ${accent} !important; }
        .text-accent { color: ${accent} !important; }
        .border-accent { border-color: ${accent} !important; }
        .bg-accent\\/10 { background-color: ${accent}1a !important; }
        .bg-accent\\/40 { background-color: ${accent}66 !important; }
        .shadow-accent\\/25 { --tw-shadow-color: ${accent}40 !important; }
        .shadow-accent\\/30 { --tw-shadow-color: ${accent}4d !important; }
        .shadow-accent\\/20 { --tw-shadow-color: ${accent}33 !important; }
        .group:hover .group-hover\\:bg-accent\\/10 { background-color: ${accent}1a !important; }
        .group:hover .group-hover\\:text-accent { color: ${accent} !important; }

        /* === BUTTON COLORS === */
        .btn-pill.bg-accent, button.bg-accent, a.bg-accent { 
          background-color: ${buttonBg} !important; 
          color: ${buttonText} !important; 
        }
        [data-testid="cta-browse-btn"] { background-color: ${buttonBg} !important; color: ${buttonText} !important; }

        /* === PRIMARY TEXT COLOR (only on light-bg sections) === */
        .text-navy-900 { color: ${textPrimary} !important; }
        .text-navy-800 { color: ${textPrimary} !important; }
        [data-testid="about-section"] h1, [data-testid="about-section"] h2,
        [data-testid="about-section"] h3, [data-testid="about-section"] h4,
        [data-testid="features-section"] h1, [data-testid="features-section"] h2,
        [data-testid="features-section"] h3, [data-testid="features-section"] h4,
        [data-testid="featured-products-section"] h1, [data-testid="featured-products-section"] h2,
        [data-testid="featured-products-section"] h3, [data-testid="featured-products-section"] h4,
        [data-testid="testimonials-section"] h1, [data-testid="testimonials-section"] h2,
        [data-testid="testimonials-section"] h3, [data-testid="testimonials-section"] h4
        { color: ${textPrimary} !important; }

        /* === SECONDARY TEXT COLOR (only on light-bg sections) === */
        [data-testid="about-section"] .text-gray-600,
        [data-testid="about-section"] .text-gray-500,
        [data-testid="about-section"] .text-gray-700,
        [data-testid="features-section"] .text-gray-600,
        [data-testid="features-section"] .text-gray-500,
        [data-testid="featured-products-section"] .text-gray-600,
        [data-testid="featured-products-section"] .text-gray-500,
        [data-testid="testimonials-section"] .text-gray-600,
        [data-testid="testimonials-section"] .text-gray-500
        { color: ${textSecondary} !important; }

        /* === LINK COLOR === */
        a:not([class*="btn"]):not([class*="bg-"]):not(nav a) { color: ${linkColor}; }

        /* === BORDER COLOR === */
        .border-gray-100 { border-color: ${borderColor} !important; }
        .border-gray-200 { border-color: ${borderColor} !important; }

        /* === HEADER === */
        [data-testid="site-header"].bg-white\\/95,
        [data-testid="site-header"].bg-white {
          background-color: ${headerBg} !important;
        }
        [data-testid="site-header"].bg-white\\/95 .text-navy-900,
        [data-testid="site-header"].bg-white .text-navy-900,
        [data-testid="site-header"] [class*="text-navy"] {
          color: ${headerText} !important;
        }

        /* === FOOTER === */
        [data-testid="site-footer"] {
          background-color: ${footerBg} !important;
          color: ${footerText} !important;
        }
        [data-testid="site-footer"] a {
          color: inherit !important;
        }
        [data-testid="site-footer"] a.text-gray-400,
        [data-testid="site-footer"] .text-gray-400 {
          color: rgb(156, 163, 175) !important;
        }

        /* === STATS SECTION (navy bg) === */
        [data-testid="stats-section"] .bg-navy-900 { background-color: ${primary} !important; }
        [data-testid="stats-section"] .text-gray-400 { color: ${textSecondary}cc !important; }
        [data-testid="stats-section"] .bg-accent { background-color: ${accent} !important; }
      `;

      // Inject font override if changed
      const font = config.font_family;
      if (font) {
        let existingStyle = doc.getElementById("gmp-design-preview-font");
        if (!existingStyle) {
          existingStyle = doc.createElement("style");
          existingStyle.id = "gmp-design-preview-font";
          doc.head.appendChild(existingStyle);
        }
        existingStyle.textContent = `body, * { font-family: '${font}', sans-serif !important; }`;
      }
    } catch (e) {
      // Cross-origin or iframe not ready — ignore
    }
  }, [config]);

  // Re-inject theme whenever config changes and iframe is loaded
  useEffect(() => {
    if (designIframeLoaded) {
      injectThemeIntoIframe();
    }
  }, [config, designIframeLoaded, injectThemeIntoIframe]);



  const restoreRevision = async (revisionId) => {
    try {
      await axios.post(`${API}/admin/site-customization/history/${revisionId}/restore`, {}, { headers });
      toast.success("Revision restored! Refreshing...");
      await fetchConfig();
      fetchHistory();
      setPreviewKey(k => k + 1);
    } catch (err) {
      toast.error("Failed to restore revision");
    }
  };

  const deleteRevision = async (revisionId) => {
    try {
      await axios.delete(`${API}/admin/site-customization/history/${revisionId}`, { headers });
      setHistory(prev => prev.filter(r => r.id !== revisionId));
      toast.success("Revision deleted");
    } catch (err) {
      toast.error("Failed to delete revision");
    }
  };

  // ===== DRAFT MODE =====
  const saveDraft = async () => {
    setDraftSaving(true);
    try {
      await axios.put(`${API}/admin/site-customization/draft`, {
        theme_colors: config.theme_colors,
        section_config: config.section_config,
        content_blocks: config.content_blocks,
        images: config.images,
      }, { headers });
      setHasDraft(true);
      toast.success("Draft saved! Your work is preserved.");
    } catch (err) {
      toast.error("Failed to save draft");
    } finally {
      setDraftSaving(false);
    }
  };

  const loadDraft = async () => {
    try {
      const res = await axios.get(`${API}/admin/site-customization/draft`, { headers });
      if (res.data.has_draft) {
        setConfig({
          theme_colors: res.data.theme_colors,
          section_config: res.data.section_config,
          content_blocks: res.data.content_blocks,
          images: res.data.images,
        });
        toast.success("Draft loaded! Review and publish when ready.");
      }
    } catch (err) {
      toast.error("Failed to load draft");
    }
  };

  const discardDraft = async () => {
    try {
      await axios.delete(`${API}/admin/site-customization/draft`, { headers });
      setHasDraft(false);
      toast.success("Draft discarded");
    } catch (err) {
      toast.error("Failed to discard draft");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className={`w-8 h-8 animate-spin ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
      </div>
    );
  }

  if (!config) return null;

  const groupedSections = {};
  Object.entries(config.section_config).forEach(([key, val]) => {
    const group = val.group || "homepage";
    if (!groupedSections[group]) groupedSections[group] = [];
    groupedSections[group].push({ key, ...val });
  });

  const dm = darkMode;

  return (
    <div className="space-y-6" data-testid="customer-view-tab">
      {viewOnly && <ViewOnlyBanner darkMode={darkMode} />}
      {/* Top bar with save/reset */}
      <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${dm ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-200"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dm ? "bg-indigo-900/40" : "bg-indigo-100"}`}>
            <Monitor className={`w-5 h-5 ${dm ? "text-indigo-400" : "text-indigo-600"}`} />
          </div>
          <div>
            <h2 className={`font-bold text-lg ${dm ? "text-gray-100" : "text-gray-900"}`} data-testid="customer-view-title">Customer View Editor</h2>
            <p className={`text-sm ${dm ? "text-gray-400" : "text-gray-500"}`}>
              Manage your site appearance, sections, and content
              {config.updated_at && (
                <span className="ml-2"> &middot; Last saved {new Date(config.updated_at).toLocaleDateString()}</span>
              )}
            </p>
          </div>
        </div>
        {!viewOnly && (
        <div className="flex items-center gap-3">
          {hasDraft && !hasChanges && (
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-500/20 text-blue-600 border-blue-500/30" data-testid="draft-badge">Draft Available</Badge>
              <Button variant="outline" size="sm" onClick={loadDraft} className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid="load-draft-btn">
                <FileDown className="w-4 h-4 mr-1" /> Resume Draft
              </Button>
              <Button variant="ghost" size="sm" onClick={discardDraft} className="rounded-xl text-red-500 hover:text-red-600" data-testid="discard-draft-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Discard
              </Button>
            </div>
          )}
          {hasChanges && (
            <>
              <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30" data-testid="unsaved-badge">Unsaved Changes</Badge>
              <Button variant="outline" size="sm" onClick={handleReset} className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid="revert-btn">
                <Undo2 className="w-4 h-4 mr-1" /> Revert
              </Button>
              <Button variant="outline" size="sm" onClick={saveDraft} disabled={draftSaving} className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid="save-draft-btn">
                {draftSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />} Save Draft
              </Button>
            </>
          )}
          <Button onClick={handleSave} disabled={saving || !hasChanges} className="rounded-xl bg-green-600 hover:bg-green-700 text-white" data-testid="save-publish-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save &amp; Publish
          </Button>
        </div>
        )}
      </div>

      {/* Sub-tabs */}
      <Tabs value={activeSubTab} onValueChange={(v) => { setActiveSubTab(v); if (v === "history") fetchHistory(); if (v === "design-preview") { setDesignTabVisited(true); fetchDecorations(); injectThemeIntoIframe(); } }}>
        <TabsList className={`w-full rounded-xl h-12 flex overflow-x-auto ${dm ? "bg-gray-800" : "bg-gray-100"}`}>
          <TabsTrigger value="preview" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-preview">
            <Eye className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Edit Content</span>
          </TabsTrigger>
          <TabsTrigger value="design-preview" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-design-preview">
            <Monitor className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Preview Design</span>
          </TabsTrigger>
          <TabsTrigger value="theme" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-theme">
            <Palette className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Theme</span>
          </TabsTrigger>
          <TabsTrigger value="sections" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-sections">
            <ToggleLeft className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Sections</span>
          </TabsTrigger>
          <TabsTrigger value="content" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-content">
            <Type className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Content</span>
          </TabsTrigger>
          <TabsTrigger value="images" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-images">
            <ImageIcon className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Images</span>
          </TabsTrigger>
          <TabsTrigger value="decorations" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-decorations">
            <PartyPopper className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Decor</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-history">
            <History className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">History</span>
          </TabsTrigger>
          <TabsTrigger value="for-schools" className="rounded-lg data-[state=active]:bg-indigo-600 data-[state=active]:text-white gap-1 text-xs flex-1 min-w-0" data-testid="subtab-for-schools">
            <School className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">Schools</span>
          </TabsTrigger>
        </TabsList>

        {/* ═══ LIVE PREVIEW ═══ */}
        <TabsContent value="preview" className="mt-6">
          <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={dm ? "text-gray-100" : ""}>Live Preview</CardTitle>
                  <CardDescription className={dm ? "text-gray-400" : ""}>
                    Click on any text to edit it directly. Changes apply to the customer-facing site when you Save &amp; Publish.
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" asChild className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`} data-testid="open-site-btn">
                    <a href={SITE_URL || "/"} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-1" /> Open Site
                    </a>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Browser chrome mock */}
              <div className={`rounded-xl overflow-hidden border-2 ${dm ? "border-gray-600" : "border-gray-300"}`}>
                <div className={`flex items-center gap-2 px-4 py-2 ${dm ? "bg-gray-700" : "bg-gray-100"}`}>
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className={`flex-1 text-center text-xs font-mono px-3 py-1 rounded-md ${dm ? "bg-gray-600 text-gray-300" : "bg-white text-gray-500"}`}>
                    {SITE_URL || window.location.origin}
                  </div>
                  {hasChanges && (
                    <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30 text-xs" data-testid="preview-unsaved-indicator">
                      Unsaved Changes
                    </Badge>
                  )}
                </div>
                <div className="max-h-[75vh] overflow-y-auto">
                  <LivePreviewEditor
                    config={config}
                    onChange={updateContent}
                    darkMode={dm}
                    sectionConfig={config.section_config}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ PREVIEW DESIGN ═══ */}
        <TabsContent value="design-preview" forceMount className={`mt-6 ${activeSubTab !== "design-preview" ? "hidden" : ""}`}>
          <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className={dm ? "text-gray-100" : ""}>Preview Design</CardTitle>
                  <CardDescription className={dm ? "text-gray-400" : ""}>
                    See how your theme, colors, fonts, and decorations look on the actual site. Fully interactive.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Viewport size toggles */}
                  {[
                    { key: "desktop", label: "Desktop", width: "100%" },
                    { key: "tablet", label: "Tablet", width: "768px" },
                    { key: "mobile", label: "Mobile", width: "375px" },
                  ].map(vp => (
                    <Button
                      key={vp.key}
                      variant={designViewport === vp.key ? "default" : "outline"}
                      size="sm"
                      onClick={() => setDesignViewport(vp.key)}
                      className={`rounded-xl text-xs ${designViewport === vp.key ? "bg-indigo-600 hover:bg-indigo-700 text-white" : (dm ? "bg-gray-700 border-gray-600 text-gray-200" : "")}`}
                      data-testid={`viewport-${vp.key}`}
                    >
                      {vp.label}
                    </Button>
                  ))}
                  <div className={`w-px h-6 ${dm ? "bg-gray-600" : "bg-gray-300"}`} />
                  {/* Decor toggle */}
                  <Button
                    variant={designDecoPreview ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setDesignDecoPreview(p => !p); if (!designDecoPreview) fetchDecorations(); }}
                    className={`rounded-xl text-xs ${designDecoPreview ? "bg-orange-600 hover:bg-orange-700 text-white" : (dm ? "bg-gray-700 border-gray-600 text-gray-200" : "")}`}
                    data-testid="design-deco-toggle"
                  >
                    <PartyPopper className="w-4 h-4 mr-1" /> {designDecoPreview ? "Hide Decor" : "Show Decor"}
                  </Button>
                  {/* Refresh */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setDesignIframeLoaded(false); setDesignPreviewKey(k => k + 1); }}
                    className={`rounded-xl text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`}
                    data-testid="design-refresh-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                  </Button>
                  {/* Open site */}
                  <Button variant="outline" size="sm" asChild className={`rounded-xl text-xs ${dm ? "bg-gray-700 border-gray-600 text-gray-200" : ""}`}>
                    <a href={SITE_URL || "/"} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-1" /> Open Site
                    </a>
                  </Button>
                </div>
              </div>
              {hasChanges && (
                <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30 text-xs mt-2" data-testid="design-unsaved-badge">
                  Unsaved theme changes are being previewed below — Save &amp; Publish to apply them for customers
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              <div className={`rounded-xl overflow-hidden border-2 ${dm ? "border-gray-600" : "border-gray-300"}`}>
                {/* Browser chrome */}
                <div className={`flex items-center gap-2 px-4 py-2 ${dm ? "bg-gray-700" : "bg-gray-100"}`}>
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className={`flex-1 text-center text-xs font-mono px-3 py-1 rounded-md ${dm ? "bg-gray-600 text-gray-300" : "bg-white text-gray-500"}`}>
                    {SITE_URL || window.location.origin}
                  </div>
                  <Badge className={`text-[10px] ${dm ? "bg-gray-600 text-gray-300" : "bg-gray-200 text-gray-600"}`}>
                    {designViewport === "desktop" ? "1920px" : designViewport === "tablet" ? "768px" : "375px"}
                  </Badge>
                </div>
                {/* Iframe container */}
                <div className="relative flex justify-center bg-gray-900/5" style={{ minHeight: "70vh" }}>
                  {!designIframeLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className={`w-8 h-8 animate-spin ${dm ? "text-gray-400" : "text-gray-500"}`} />
                        <p className={`text-sm ${dm ? "text-gray-400" : "text-gray-500"}`}>Loading preview...</p>
                      </div>
                    </div>
                  )}
                  <iframe
                    key={designPreviewKey}
                    ref={designIframeRef}
                    src={designTabVisited ? (SITE_URL || "/") : "about:blank"}
                    title="Design Preview"
                    className={`bg-white transition-all duration-300 ${!designIframeLoaded ? "opacity-0" : "opacity-100"}`}
                    style={{
                      width: designViewport === "desktop" ? "100%" : designViewport === "tablet" ? "768px" : "375px",
                      height: "70vh",
                      border: "none",
                      maxWidth: "100%",
                    }}
                    onLoad={() => {
                      setDesignIframeLoaded(true);
                      // Re-inject theme multiple times to ensure it persists after iframe's own API fetch
                      const applyPreview = () => {
                        injectThemeIntoIframe();
                        if (config && designIframeRef.current?.contentWindow) {
                          designIframeRef.current.contentWindow.postMessage({
                            type: "SITE_CUSTOMIZATION_PREVIEW",
                            payload: {
                              theme_colors: config.theme_colors,
                              section_config: config.section_config,
                              content_blocks: config.content_blocks,
                              images: config.images,
                            },
                          }, "*");
                        }
                      };
                      setTimeout(applyPreview, 300);
                      setTimeout(applyPreview, 1200);
                      setTimeout(applyPreview, 2500);
                    }}
                    data-testid="design-preview-iframe"
                  />
                  {designDecoPreview && <DecorationPreviewOverlay decorations={previewDecorations} darkMode={dm} onToggle={toggleDecorationFromPreview} viewOnly={viewOnly} />}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>



        {/* ═══ THEME EDITOR ═══ */}
        <TabsContent value="theme" className="mt-6 space-y-6">
          {/* Global Colors */}
          <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dm ? "bg-purple-900/40" : "bg-purple-100"}`}>
                  <Paintbrush className={`w-5 h-5 ${dm ? "text-purple-400" : "text-purple-600"}`} />
                </div>
                <div>
                  <CardTitle className={dm ? "text-gray-100" : ""}>Global Color Palette</CardTitle>
                  <CardDescription className={dm ? "text-gray-400" : ""}>
                    These colors apply site-wide across all pages and components
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {Object.entries(COLOR_FIELDS).map(([groupName, fields]) => (
                <div key={groupName}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wider mb-4 ${dm ? "text-gray-300" : "text-gray-500"}`}>
                    {groupName}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(fields).map(([key, label]) => (
                      <div
                        key={key}
                        className={`flex items-center gap-3 p-3 rounded-xl border ${dm ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="relative">
                          <input
                            type="color"
                            value={config.theme_colors.global[key] || "#000000"}
                            onChange={(e) => updateGlobalColor(key, e.target.value)}
                            className="w-10 h-10 rounded-lg border-2 cursor-pointer"
                            style={{ borderColor: config.theme_colors.global[key] || "#000000" }}
                            disabled={viewOnly}
                            data-testid={`color-${key}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${dm ? "text-gray-200" : "text-gray-700"}`}>{label}</p>
                          <p className={`text-xs font-mono ${dm ? "text-gray-400" : "text-gray-500"}`}>
                            {config.theme_colors.global[key] || "#000000"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Section Overrides */}
          <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader>
              <CardTitle className={dm ? "text-gray-100" : ""}>Section Color Overrides</CardTitle>
              <CardDescription className={dm ? "text-gray-400" : ""}>
                Customize colors for individual sections without affecting global settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!viewOnly && (
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(config.section_config).map(([key, val]) => (
                  <Button
                    key={key}
                    variant={editingOverride === key ? "default" : "outline"}
                    size="sm"
                    onClick={() => setEditingOverride(editingOverride === key ? null : key)}
                    className={`rounded-lg text-xs ${editingOverride === key ? "bg-indigo-600 text-white" : (dm ? "bg-gray-700 border-gray-600 text-gray-300" : "")}`}
                    data-testid={`override-btn-${key}`}
                  >
                    {val.label || key}
                    {config.theme_colors.overrides?.[key] && (
                      <div className="w-2 h-2 rounded-full bg-green-400 ml-1" />
                    )}
                  </Button>
                ))}
              </div>
              )}

              {editingOverride && (
                <div className={`p-4 rounded-xl border-2 ${dm ? "bg-gray-700/50 border-indigo-700/50" : "bg-indigo-50/50 border-indigo-200"}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={`font-semibold ${dm ? "text-gray-100" : ""}`}>
                      Override: {config.section_config[editingOverride]?.label || editingOverride}
                    </h4>
                    {!viewOnly && config.theme_colors.overrides?.[editingOverride] && (
                      <Button variant="destructive" size="sm" onClick={() => removeSectionOverride(editingOverride)} className="rounded-lg text-xs">
                        Remove Override
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {["background", "text_primary", "accent", "button_bg", "button_text", "border"].map(ck => (
                      <div key={ck} className={`flex items-center gap-2 p-2 rounded-lg ${dm ? "bg-gray-600/50" : "bg-white"}`}>
                        <input
                          type="color"
                          value={config.theme_colors.overrides?.[editingOverride]?.[ck] || config.theme_colors.global[ck] || "#000000"}
                          onChange={(e) => updateSectionOverride(editingOverride, ck, e.target.value)}
                          className="w-8 h-8 rounded cursor-pointer border"
                          disabled={viewOnly}
                          data-testid={`override-color-${editingOverride}-${ck}`}
                        />
                        <span className={`text-xs ${dm ? "text-gray-300" : "text-gray-600"}`}>{ck.replace(/_/g, " ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!editingOverride && Object.keys(config.theme_colors.overrides || {}).length === 0 && (
                <p className={`text-sm text-center py-6 ${dm ? "text-gray-400" : "text-gray-500"}`}>
                  No section overrides configured. Click a section above to add custom colors.
                </p>
              )}

              {!editingOverride && Object.keys(config.theme_colors.overrides || {}).length > 0 && (
                <div className="space-y-2">
                  {Object.entries(config.theme_colors.overrides).map(([secKey, colors]) => (
                    <div key={secKey} className={`flex items-center justify-between p-3 rounded-xl ${dm ? "bg-gray-700/50" : "bg-gray-50"}`}>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className={`text-sm font-medium ${dm ? "text-gray-200" : ""}`}>
                          {config.section_config[secKey]?.label || secKey}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {Object.values(colors).slice(0, 4).map((c, i) => (
                          <div key={i} className="w-5 h-5 rounded border" style={{ backgroundColor: c }} />
                        ))}
                        {Object.values(colors).length > 4 && (
                          <span className={`text-xs ml-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>
                            +{Object.values(colors).length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ SECTION TOGGLES ═══ */}
        <TabsContent value="sections" className="mt-6 space-y-6">
          {Object.entries(SECTION_GROUPS).map(([groupKey, groupMeta]) => {
            const sections = groupedSections[groupKey] || [];
            if (sections.length === 0) return null;
            const GroupIcon = groupMeta.icon;
            return (
              <Card key={groupKey} className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dm ? "bg-emerald-900/40" : "bg-emerald-100"}`}>
                      <GroupIcon className={`w-5 h-5 ${dm ? "text-emerald-400" : "text-emerald-600"}`} />
                    </div>
                    <div>
                      <CardTitle className={dm ? "text-gray-100" : ""}>{groupMeta.label}</CardTitle>
                      <CardDescription className={dm ? "text-gray-400" : ""}>
                        {sections.filter(s => s.enabled).length} of {sections.length} sections enabled
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {sections.map((section) => (
                      <div
                        key={section.key}
                        className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                          section.enabled
                            ? (dm ? "bg-gray-700/50 border-gray-600" : "bg-white border-gray-200")
                            : (dm ? "bg-gray-800/50 border-gray-700 opacity-60" : "bg-gray-50 border-gray-200 opacity-60")
                        }`}
                        data-testid={`section-toggle-row-${section.key}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${section.enabled ? "bg-green-500" : "bg-gray-400"}`} />
                          <div>
                            <p className={`text-sm font-medium ${dm ? "text-gray-200" : "text-gray-700"}`}>
                              {section.label || section.key}
                            </p>
                          </div>
                        </div>
                        <Switch
                          checked={section.enabled}
                          onCheckedChange={() => toggleSection(section.key)}
                          disabled={viewOnly}
                          data-testid={`section-toggle-${section.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* ═══ CONTENT EDITOR ═══ */}
        <TabsContent value="content" className="mt-6">
          <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dm ? "bg-amber-900/40" : "bg-amber-100"}`}>
                  <Type className={`w-5 h-5 ${dm ? "text-amber-400" : "text-amber-600"}`} />
                </div>
                <div>
                  <CardTitle className={dm ? "text-gray-100" : ""}>Content Blocks</CardTitle>
                  <CardDescription className={dm ? "text-gray-400" : ""}>
                    Edit the text content displayed on your homepage
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ContentBlockEditor
                blocks={config.content_blocks}
                onChange={updateContent}
                darkMode={dm}
                viewOnly={viewOnly}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ IMAGES ═══ */}
        <TabsContent value="images" className="mt-6">
          <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dm ? "bg-pink-900/40" : "bg-pink-100"}`}>
                  <ImageIcon className={`w-5 h-5 ${dm ? "text-pink-400" : "text-pink-600"}`} />
                </div>
                <div>
                  <CardTitle className={dm ? "text-gray-100" : ""}>Homepage Images</CardTitle>
                  <CardDescription className={dm ? "text-gray-400" : ""}>
                    Upload or replace the key visual images on your homepage
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ImageManager
                images={config.images || {}}
                onUpload={handleImageUpload}
                onRemove={removeImage}
                uploading={uploading}
                darkMode={dm}
                viewOnly={viewOnly}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ DECORATIONS ═══ */}
        <TabsContent value="decorations" className="mt-6">
          <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dm ? "bg-orange-900/40" : "bg-orange-100"}`}>
                  <PartyPopper className={`w-5 h-5 ${dm ? "text-orange-400" : "text-orange-600"}`} />
                </div>
                <div>
                  <CardTitle className={dm ? "text-gray-100" : ""}>Seasonal Decorations</CardTitle>
                  <CardDescription className={dm ? "text-gray-400" : ""}>
                    Create festive banners, stickers, and accents for holidays. Schedule them to auto-activate.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <DecorationsManager darkMode={dm} viewOnly={viewOnly} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ HISTORY ═══ */}
        <TabsContent value="history" className="mt-6">
          <Card className={`rounded-2xl border-2 ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${dm ? "bg-teal-900/40" : "bg-teal-100"}`}>
                  <History className={`w-5 h-5 ${dm ? "text-teal-400" : "text-teal-600"}`} />
                </div>
                <div>
                  <CardTitle className={dm ? "text-gray-100" : ""}>Revision History</CardTitle>
                  <CardDescription className={dm ? "text-gray-400" : ""}>
                    Every "Save & Publish" creates a snapshot. Restore any previous version with one click.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <RevisionHistory
                history={history}
                loading={historyLoading}
                onRestore={restoreRevision}
                onDelete={deleteRevision}
                darkMode={dm}
                viewOnly={viewOnly}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ FOR SCHOOLS ═══ */}
        <TabsContent value="for-schools" className="mt-6">
          <ForSchoolsCMS darkMode={dm} token={token} viewOnly={viewOnly} />
        </TabsContent>

      </Tabs>
    </div>
  );
};

/* Group content blocks by section */
const CONTENT_SECTIONS = [
  {
    title: "Hero Section",
    keys: ["hero_badge", "hero_title", "hero_highlight", "hero_subtitle", "hero_cta_primary", "hero_cta_secondary"],
  },
  {
    title: "Statistics",
    keys: ["stat_1_value", "stat_1_label", "stat_2_value", "stat_2_label", "stat_3_value", "stat_3_label", "stat_4_value", "stat_4_label"],
  },
  {
    title: "About Us",
    keys: ["about_label", "about_title", "about_description"],
  },
  {
    title: "Features / Why Choose Us",
    keys: ["features_label", "features_title", "features_subtitle", "feature_1_title", "feature_1_desc", "feature_2_title", "feature_2_desc", "feature_3_title", "feature_3_desc", "feature_4_title", "feature_4_desc"],
  },
  {
    title: "Featured Products",
    keys: ["featured_label", "featured_title"],
  },
  {
    title: "Testimonials",
    keys: ["testimonials_label", "testimonials_title", "testimonial_1_name", "testimonial_1_role", "testimonial_1_content", "testimonial_2_name", "testimonial_2_role", "testimonial_2_content", "testimonial_3_name", "testimonial_3_role", "testimonial_3_content"],
  },
  {
    title: "Call to Action",
    keys: ["cta_title", "cta_subtitle", "cta_primary", "cta_secondary"],
  },
  {
    title: "Success Stories - Hero",
    keys: ["ss_hero_badge", "ss_hero_title", "ss_hero_highlight", "ss_hero_subtitle", "ss_hero_cta"],
  },
  {
    title: "Success Stories - Stats",
    keys: ["ss_stat_1_value", "ss_stat_1_label", "ss_stat_2_value", "ss_stat_2_label", "ss_stat_3_value", "ss_stat_3_label", "ss_stat_4_value", "ss_stat_4_label"],
  },
  {
    title: "Success Stories - Schools",
    keys: ["ss_schools_label", "ss_schools_title", "ss_schools_subtitle",
      "ss_school_1_name", "ss_school_1_location", "ss_school_1_before", "ss_school_1_after", "ss_school_1_students", "ss_school_1_year", "ss_school_1_quote", "ss_school_1_contact",
      "ss_school_2_name", "ss_school_2_location", "ss_school_2_before", "ss_school_2_after", "ss_school_2_students", "ss_school_2_year", "ss_school_2_quote", "ss_school_2_contact",
      "ss_school_3_name", "ss_school_3_location", "ss_school_3_before", "ss_school_3_after", "ss_school_3_students", "ss_school_3_year", "ss_school_3_quote", "ss_school_3_contact"],
  },
  {
    title: "Success Stories - Students",
    keys: ["ss_students_label", "ss_students_title",
      "ss_student_1_name", "ss_student_1_school", "ss_student_1_before", "ss_student_1_after", "ss_student_1_subjects", "ss_student_1_quote",
      "ss_student_2_name", "ss_student_2_school", "ss_student_2_before", "ss_student_2_after", "ss_student_2_subjects", "ss_student_2_quote",
      "ss_student_3_name", "ss_student_3_school", "ss_student_3_before", "ss_student_3_after", "ss_student_3_subjects", "ss_student_3_quote",
      "ss_student_4_name", "ss_student_4_school", "ss_student_4_before", "ss_student_4_after", "ss_student_4_subjects", "ss_student_4_quote"],
  },
  {
    title: "Success Stories - CTA",
    keys: ["ss_cta_title", "ss_cta_subtitle", "ss_cta_primary", "ss_cta_secondary"],
  },
  {
    title: "Footer",
    keys: ["footer_brand_name", "footer_brand_desc", "footer_contact_heading", "footer_address", "footer_phone", "footer_email", "footer_newsletter_title", "footer_newsletter_desc", "footer_copyright", "footer_syllabus_note"],
  },
];

function ContentBlockEditor({ blocks, onChange, darkMode: dm, viewOnly }) {
  const friendlyName = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div className="space-y-6">
      {CONTENT_SECTIONS.map((section) => (
        <div key={section.title}>
          <h3 className={`text-sm font-semibold uppercase tracking-wider mb-3 ${dm ? "text-gray-300" : "text-gray-500"}`}>
            {section.title}
          </h3>
          <div className="space-y-3">
            {section.keys.map((key) => {
              const value = blocks[key] || "";
              const isLong = value.length > 60 || key.includes("description") || key.includes("subtitle");
              return (
                <div key={key} className={`p-3 rounded-xl border ${dm ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"}`}>
                  <label className={`block text-xs font-medium mb-1.5 ${dm ? "text-gray-400" : "text-gray-500"}`}>
                    {friendlyName(key)}
                  </label>
                  {isLong ? (
                    <Textarea
                      value={value}
                      onChange={(e) => onChange(key, e.target.value)}
                      rows={3}
                      className={`rounded-lg text-sm ${dm ? "bg-gray-600 border-gray-500 text-gray-100 placeholder:text-gray-400" : ""}`}
                      disabled={viewOnly}
                      data-testid={`content-${key}`}
                    />
                  ) : (
                    <Input
                      value={value}
                      onChange={(e) => onChange(key, e.target.value)}
                      className={`rounded-lg text-sm ${dm ? "bg-gray-600 border-gray-500 text-gray-100 placeholder:text-gray-400" : ""}`}
                      disabled={viewOnly}
                      data-testid={`content-${key}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}


/* ═══ IMAGE MANAGER ═══ */
const IMAGE_SLOTS = [
  { key: "hero", label: "Hero Background", description: "Main homepage hero banner background (recommended: 1920x1080)" },
  { key: "successHero", label: "Success Stories Hero", description: "\"Real Students. Real Results.\" page banner (recommended: 1920x1080)" },
  { key: "classroom", label: "Classroom Photo", description: "About section — classroom image (recommended: 600x400)" },
  { key: "students", label: "Students Photo", description: "About section — students image (recommended: 600x400)" },
  { key: "examHall", label: "Exam Hall / Pattern", description: "Statistics & CTA section background pattern (recommended: 1920x1080)" },
  { key: "studentJourneys", label: "Student Journeys", description: "Success Stories — \"From Struggling to Excelling\" section image (recommended: 600x500)" },
  { key: "happyStudents", label: "Success Stories", description: "Testimonials section — success stories image (recommended: 600x400)" },
  { key: "schoolServicesBg", label: "School Services Background", description: "School Services page — hero background image (recommended: 1920x1080)" },
  { key: "schoolServicesPhoto", label: "School Services Photo", description: "School Services page — students in classroom photo (recommended: 600x500)" },
];

const DEFAULT_SITE_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/n4jmpo8p_WhatsApp%20Image%202026-02-09%20at%209.31.47%20PM.jpeg",
  successHero: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/6a54yvph_WhatsApp%20Image%202026-02-09%20at%2011.16.41%20PM.jpeg",
  classroom: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/22gq6ict_WhatsApp%20Image%202026-02-09%20at%209.41.16%20PM.jpeg",
  students: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/qgz5ug4l_WhatsApp%20Image%202026-02-09%20at%2011.03.36%20PM.jpeg",
  examHall: "https://customer-assets.emergentagent.com/job_doc-deploy-2/artifacts/t2tw76x1_WhatsApp%20Image%202026-02-09%20at%2011.10.54%20PM.jpeg",
  studentJourneys: "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/aw9tl6vm_WhatsApp%20Image%202026-02-10%20at%204.57.23%20PM.jpeg",
  happyStudents: "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/9o1fdu8q_WhatsApp%20Image%202026-02-10%20at%204.33.30%20PM.jpeg",
  schoolServicesBg: "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/smq7dle8_image.png",
  schoolServicesPhoto: "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/7m2f21ff_WhatsApp%20Image%202026-02-09%20at%209.46.57%20PM.jpeg",
};

function ImageManager({ images, onUpload, onRemove, uploading, darkMode: dm, viewOnly }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {IMAGE_SLOTS.map(({ key, label, description }) => {
        const customUrl = images[key];
        const defaultUrl = DEFAULT_SITE_IMAGES[key];
        const displayUrl = customUrl || defaultUrl;
        const isDefault = !customUrl && defaultUrl;
        return (
          <div
            key={key}
            className={`rounded-xl border-2 overflow-hidden ${dm ? "bg-gray-700/50 border-gray-600" : "bg-gray-50 border-gray-200"}`}
            data-testid={`image-slot-${key}`}
          >
            {/* Preview */}
            <div className={`relative h-40 flex items-center justify-center ${dm ? "bg-gray-800" : "bg-gray-100"}`}>
              {displayUrl ? (
                <>
                  <img src={displayUrl} alt={label} className="w-full h-full object-cover" />
                  {isDefault && (
                    <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-semibold ${dm ? "bg-gray-900/70 text-gray-300" : "bg-black/50 text-white"}`}>
                      Current default
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center">
                  <ImageIcon className={`w-10 h-10 mx-auto mb-2 ${dm ? "text-gray-600" : "text-gray-300"}`} />
                  <p className={`text-xs ${dm ? "text-gray-500" : "text-gray-400"}`}>No image set</p>
                </div>
              )}
              {customUrl && !viewOnly && (
                <button
                  onClick={() => onRemove(key)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                  title="Remove custom image (revert to default)"
                  data-testid={`remove-image-${key}`}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {/* Info + Upload */}
            <div className="p-3">
              <p className={`text-sm font-medium ${dm ? "text-gray-200" : "text-gray-700"}`}>{label}</p>
              <p className={`text-xs mb-3 ${dm ? "text-gray-400" : "text-gray-500"}`}>{description}</p>
              {!viewOnly && (
                <label
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                    uploading === key
                      ? (dm ? "bg-gray-600 text-gray-400" : "bg-gray-200 text-gray-500")
                      : (dm ? "bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60" : "bg-indigo-100 text-indigo-700 hover:bg-indigo-200")
                  }`}
                  data-testid={`upload-image-${key}`}
                >
                  {uploading === key ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  {uploading === key ? "Uploading..." : customUrl ? "Replace Image" : "Upload Image"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.[0]) onUpload(key, e.target.files[0]);
                      e.target.value = "";
                    }}
                    disabled={uploading === key}
                  />
                </label>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══ REVISION HISTORY ═══ */
function RevisionHistory({ history, loading, onRestore, onDelete, darkMode: dm, viewOnly }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className={`w-6 h-6 animate-spin ${dm ? "text-gray-400" : "text-gray-500"}`} />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12">
        <History className={`w-12 h-12 mx-auto mb-3 ${dm ? "text-gray-600" : "text-gray-300"}`} />
        <h3 className={`font-semibold mb-1 ${dm ? "text-gray-300" : "text-gray-600"}`}>No revisions yet</h3>
        <p className={`text-sm ${dm ? "text-gray-500" : "text-gray-400"}`}>
          Each time you "Save & Publish", a snapshot of the current design will be saved here.
        </p>
      </div>
    );
  }

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
      });
    } catch { return iso; }
  };

  return (
    <div className="space-y-3" data-testid="revision-list">
      {history.map((rev, idx) => (
        <div
          key={rev.id}
          className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
            dm ? "bg-gray-700/50 border-gray-600" : "bg-white border-gray-200"
          }`}
          data-testid={`revision-${rev.id}`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center font-mono text-sm font-bold ${
              dm ? "bg-gray-600 text-gray-300" : "bg-gray-100 text-gray-500"
            }`}>
              {idx + 1}
            </div>
            <div>
              <p className={`text-sm font-medium ${dm ? "text-gray-200" : "text-gray-700"}`}>
                {rev.label || `Version ${history.length - idx}`}
              </p>
              <p className={`text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>
                {formatDate(rev.saved_at)} {rev.saved_by ? `by ${rev.saved_by}` : ""}
              </p>
            </div>
          </div>
          {!viewOnly && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRestore(rev.id)}
              className={`rounded-lg text-xs ${dm ? "bg-gray-600 border-gray-500 text-gray-200 hover:bg-gray-500" : ""}`}
              data-testid={`restore-revision-${rev.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(rev.id)}
              className={`rounded-lg text-xs h-8 w-8 p-0 ${dm ? "text-gray-400 hover:text-red-400" : "text-gray-400 hover:text-red-600"}`}
              data-testid={`delete-revision-${rev.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
          )}
        </div>
      ))}
    </div>
  );
}


/* ═══ DECORATION PREVIEW OVERLAY ═══ */
const HOLIDAY_PRESETS_MAP = {
  christmas: "\u{1F384}", "new-year": "\u{1F386}", easter: "\u{1F423}",
  valentine: "\u{2764}\u{FE0F}", independence: "\u{1F1EC}\u{1F1ED}", constitution: "\u{1F4DC}",
  "may-day": "\u{1F528}", "au-day": "\u{1F30D}", republic: "\u{1F3DB}\u{FE0F}",
  founders: "\u{2B50}", farmers: "\u{1F33E}", eid: "\u{1F319}",
};

function DecorationPreviewOverlay({ decorations, darkMode: dm, onToggle, viewOnly }) {
  if (!decorations || decorations.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid="deco-preview-empty">
        <div className={`px-6 py-4 rounded-2xl text-center ${dm ? "bg-gray-800/90 text-gray-300" : "bg-white/90 text-gray-600"} shadow-xl border ${dm ? "border-gray-600" : "border-gray-200"}`}>
          <PartyPopper className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm font-medium">No decorations created yet</p>
          <p className={`text-xs mt-1 ${dm ? "text-gray-400" : "text-gray-500"}`}>Go to the Decor tab to create festive decorations</p>
        </div>
      </div>
    );
  }

  const banners = decorations.filter(d => d.position === "top-banner");
  const corners = decorations.filter(d => d.position?.startsWith("corner-"));
  const floating = decorations.filter(d => d.position?.startsWith("floating-"));

  const posMap = {
    "corner-top-left": "top-3 left-3",
    "corner-top-right": "top-3 right-3",
    "corner-bottom-left": "bottom-3 left-3",
    "corner-bottom-right": "bottom-3 right-3",
  };

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="deco-preview-overlay">
      {/* Top Banners stacked */}
      <div className="absolute top-0 left-0 right-0 z-10">
        {banners.map(dec => {
          const emoji = HOLIDAY_PRESETS_MAP[dec.holiday] || "";
          return (
            <div
              key={dec.id}
              className="relative w-full flex items-center justify-center text-sm font-medium"
              style={{
                backgroundColor: dec.styles?.background_color || "#1a1a2e",
                color: dec.styles?.text_color || "#ffffff",
                height: dec.styles?.height || "50px",
                opacity: dec.is_active ? (dec.styles?.opacity ?? 1) : 0.5,
                backgroundImage: dec.image_url ? `url(${resolveImageUrl(dec.image_url)})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
              data-testid={`deco-preview-banner-${dec.id}`}
            >
              <span className="px-4 py-1 rounded-md" style={{
                backgroundColor: dec.image_url ? `${dec.styles?.background_color || "#1a1a2e"}bb` : "transparent"
              }}>
                {emoji && <span className="mr-1.5">{emoji}</span>}
                {dec.styles?.text || dec.name}
              </span>
              {/* Interactive toggle */}
              {!viewOnly && (
                <button
                  onClick={() => onToggle(dec.id, dec.is_active)}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase transition-all cursor-pointer hover:scale-105 ${
                    dec.is_active
                      ? "bg-green-500 text-white hover:bg-green-600"
                      : "bg-gray-700/80 text-gray-300 hover:bg-gray-600"
                  }`}
                  data-testid={`deco-toggle-banner-${dec.id}`}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${dec.is_active ? "bg-white" : "bg-gray-500"}`} />
                  {dec.is_active ? "Active" : "Inactive"}
                </button>
              )}
              {viewOnly && (
                <span className={`absolute right-2 top-1/2 -translate-y-1/2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                  dec.is_active ? "bg-green-500/80 text-white" : "bg-gray-800/60 text-gray-300"
                }`}>
                  {dec.is_active ? "Active" : "Inactive"}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Corner Decorations */}
      {corners.map(dec => (
        <div
          key={dec.id}
          className={`absolute ${posMap[dec.position] || "top-3 right-3"} z-20`}
          style={{ opacity: dec.is_active ? (dec.styles?.opacity ?? 1) : 0.45 }}
          data-testid={`deco-preview-corner-${dec.id}`}
        >
          {dec.image_url ? (
            <img src={resolveImageUrl(dec.image_url)} alt={dec.name} className="w-16 h-16 object-contain drop-shadow-lg" />
          ) : (
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xs font-bold shadow-lg"
              style={{ backgroundColor: dec.styles?.background_color || "#c41e3a" }}
            >
              {dec.styles?.text?.slice(0, 8) || dec.name?.slice(0, 8)}
            </div>
          )}
          {!viewOnly ? (
            <button
              onClick={() => onToggle(dec.id, dec.is_active)}
              className={`absolute -bottom-5 left-1/2 -translate-x-1/2 pointer-events-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase whitespace-nowrap cursor-pointer transition-all hover:scale-110 ${
                dec.is_active ? "bg-green-500 text-white hover:bg-green-600" : "bg-gray-700/80 text-gray-300 hover:bg-gray-600"
              }`}
              data-testid={`deco-toggle-corner-${dec.id}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${dec.is_active ? "bg-white" : "bg-gray-500"}`} />
              {dec.is_active ? "On" : "Off"}
            </button>
          ) : (
            <span className={`absolute -bottom-4 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase whitespace-nowrap ${
              dec.is_active ? "bg-green-500/80 text-white" : "bg-gray-800/70 text-gray-300"
            }`}>
              {dec.is_active ? "On" : "Off"}
            </span>
          )}
        </div>
      ))}

      {/* Floating Decorations */}
      {floating.map(dec => {
        const isLeft = dec.position === "floating-left";
        return (
          <div
            key={dec.id}
            className={`absolute ${isLeft ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 z-20`}
            style={{ opacity: dec.is_active ? (dec.styles?.opacity ?? 0.85) : 0.4 }}
            data-testid={`deco-preview-floating-${dec.id}`}
          >
            {dec.image_url ? (
              <img src={resolveImageUrl(dec.image_url)} alt={dec.name} className="w-14 h-auto object-contain drop-shadow-lg" />
            ) : (
              <div
                className="w-10 h-20 rounded-xl flex items-center justify-center text-white text-xs font-bold shadow-lg"
                style={{
                  backgroundColor: dec.styles?.background_color || "#c41e3a",
                  writingMode: "vertical-rl",
                }}
              >
                {dec.styles?.text || dec.name}
              </div>
            )}
            {!viewOnly ? (
              <button
                onClick={() => onToggle(dec.id, dec.is_active)}
                className={`absolute -bottom-5 left-1/2 -translate-x-1/2 pointer-events-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase whitespace-nowrap cursor-pointer transition-all hover:scale-110 ${
                  dec.is_active ? "bg-green-500 text-white hover:bg-green-600" : "bg-gray-700/80 text-gray-300 hover:bg-gray-600"
                }`}
                data-testid={`deco-toggle-floating-${dec.id}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${dec.is_active ? "bg-white" : "bg-gray-500"}`} />
                {dec.is_active ? "On" : "Off"}
              </button>
            ) : (
              <span className={`absolute -bottom-4 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase whitespace-nowrap ${
                dec.is_active ? "bg-green-500/80 text-white" : "bg-gray-800/70 text-gray-300"
              }`}>
                {dec.is_active ? "On" : "Off"}
              </span>
            )}
          </div>
        );
      })}

      {/* Legend */}
      <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-4 py-2 rounded-xl shadow-lg pointer-events-auto ${
        dm ? "bg-gray-900/90 border border-gray-700" : "bg-white/90 border border-gray-200"
      }`} data-testid="deco-preview-legend">
        <span className={`text-xs font-medium ${dm ? "text-gray-300" : "text-gray-600"}`}>
          {decorations.length} decoration{decorations.length !== 1 ? "s" : ""}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span className={`text-[10px] ${dm ? "text-gray-400" : "text-gray-500"}`}>{decorations.filter(d => d.is_active).length} Active</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-gray-500" />
          <span className={`text-[10px] ${dm ? "text-gray-400" : "text-gray-500"}`}>{decorations.filter(d => !d.is_active).length} Off</span>
        </span>
      </div>
    </div>
  );
}
